import { X, Search, UserCircle2 } from "lucide-react";
import React, { useState, useEffect, useRef, useCallback } from "react";

import { getUsersWithRoles, UserWithRoleType } from "@/api/role-user";
import { MoonLoader } from "react-spinners";

const API_URL = import.meta.env.VITE_API_URL as string;
const resolve_image = (url?: string | null) =>
  url
    ? url.startsWith("http")
      ? url
      : `${API_URL}/${url.replace(/^[\/\\]/, "")}`
    : undefined;

const PAGE_SIZE = 50;

export interface StaffManagerType {
  id?: string;
  roleId: number;
  roleName: string;
  userId: number;
  userName: string;
  userImageUrl?: string;
}

/** Unique key for a user+role combination */
const itemKey = (userId: number, roleId: number) => `${userId}-${roleId}`;

interface StaffManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (staffManagers: StaffManagerType[]) => void;
  title: string;
  existingRoles?: StaffManagerType[];
}

const StaffManagerModal: React.FC<StaffManagerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  existingRoles = []
}) => {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [searchInput, setSearchInput] = useState("");
  const [items, setItems] = useState<UserWithRoleType[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);
  const currentPageRef = useRef(0);
  const loadedCountRef = useRef(0);
  const totalCountRef = useRef(0);
  const requestIdRef = useRef(0);
  const searchRef = useRef("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchPage = useCallback(
    async (
      pageNum: number,
      search: string,
      requestId: number,
      reset: boolean
    ) => {
      if (isFetchingRef.current && !reset) return;
      isFetchingRef.current = true;
      setIsFetching(true);

      try {
        const result = await getUsersWithRoles({
          pageSize: PAGE_SIZE,
          pageNumber: pageNum,
          search
        });

        // Discard stale responses from superseded searches
        if (requestId !== requestIdRef.current) return;

        if (result) {
          const newItems = (result.data as UserWithRoleType[]) ?? [];
          if (reset) {
            setItems(newItems);
            loadedCountRef.current = newItems.length;
          } else {
            setItems((prev) => {
              const updated = [...prev, ...newItems];
              loadedCountRef.current = updated.length;
              return updated;
            });
          }
          totalCountRef.current = result.totalCount ?? 0;
          setTotalCount(result.totalCount ?? 0);
          currentPageRef.current = pageNum;
        }
      } finally {
        if (requestId === requestIdRef.current) {
          isFetchingRef.current = false;
          setIsFetching(false);
        }
      }
    },
    []
  );

  // Reset and load when modal opens
  useEffect(() => {
    if (!isOpen) return;

    requestIdRef.current += 1;
    const id = requestIdRef.current;
    isFetchingRef.current = false;
    currentPageRef.current = 0;
    loadedCountRef.current = 0;
    totalCountRef.current = 0;
    searchRef.current = "";
    setSearchInput("");
    setItems([]);
    setTotalCount(0);
    setSelectedKeys(new Set());

    fetchPage(1, "", id, true);
  }, [isOpen, fetchPage]);

  // Debounced search: cancel pending timer, wait 500ms, then fetch
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchInput(val);

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      searchRef.current = val;

      // New request generation - stale in-flight responses will be discarded
      requestIdRef.current += 1;
      const id = requestIdRef.current;
      isFetchingRef.current = false;
      currentPageRef.current = 0;
      loadedCountRef.current = 0;
      totalCountRef.current = 0;
      setItems([]);
      setTotalCount(0);

      fetchPage(1, val, id, true);
    }, 500);
  };

  // Infinite scroll: when near bottom of list, fetch next page
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || isFetchingRef.current) return;
    if (loadedCountRef.current >= totalCountRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      const nextPage = currentPageRef.current + 1;
      requestIdRef.current += 1;
      const id = requestIdRef.current;
      fetchPage(nextPage, searchRef.current, id, false);
    }
  }, [fetchPage]);

  const toggleSelection = (item: UserWithRoleType) => {
    const key = itemKey(item.userId, item.roleId);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = () => {
    const selected: StaffManagerType[] = items
      .filter((item) => selectedKeys.has(itemKey(item.userId, item.roleId)))
      .map((item) => ({
        id: `${Date.now()}-${item.userId}-${item.roleId}`,
        roleId: item.roleId,
        roleName: item.roleNameFa || item.roleNameEn || "",
        userId: item.userId,
        userName: item.fullName || item.userName || "",
        userImageUrl: item.imageUrl || undefined
      }));

    onSave(selected);
    onClose();
  };

  const existingKeySet = new Set(
    existingRoles.map((r) => itemKey(r.userId, r.roleId))
  );
  const hasMore = loadedCountRef.current < totalCount;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4">
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
          <h2 className="text-base font-semibold text-gray-800">{title}</h2>
        </div>

        {/* Search */}
        <div className="flex-shrink-0 border-b border-gray-100 px-5 py-3">
          <div className="relative">
            <Search
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder="جستجوی کاربر یا نقش..."
              value={searchInput}
              onChange={handleSearchChange}
              className="w-full rounded-lg border border-gray-300 py-2 pl-4 pr-9 text-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              dir="rtl"
            />
          </div>
        </div>

        {/* List */}
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="min-h-0 flex-1 overflow-y-auto"
        >
          {isFetching && items.length === 0 ? (
            <div className="flex justify-center py-10">
              <MoonLoader color="#2563EB" size={28} />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">
              موردی یافت نشد
            </div>
          ) : (
            <>
              {items.map((item) => {
                const key = itemKey(item.userId, item.roleId);
                const isSelected = selectedKeys.has(key);
                const isExisting = existingKeySet.has(key);

                return (
                  <div
                    key={key}
                    dir="rtl"
                    onClick={() => !isExisting && toggleSelection(item)}
                    className={`flex cursor-pointer items-center gap-3 border-b border-gray-100 px-5 py-3 last:border-b-0 ${
                      isExisting
                        ? "cursor-not-allowed bg-gray-50 opacity-60"
                        : isSelected
                          ? "bg-blue-50"
                          : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {item.imageUrl ? (
                        <img
                          src={resolve_image(item.imageUrl)}
                          alt={item.fullName || item.userName || ""}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <UserCircle2 className="text-gray-300" size={40} />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-gray-900">
                        {item.fullName || item.userName}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-gray-500">
                        {item.roleNameFa || item.roleNameEn}
                      </p>
                    </div>

                    <div
                      className={`ml-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors ${
                        isExisting
                          ? "border-gray-400 bg-gray-400"
                          : isSelected
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-300 bg-white"
                      }`}
                    >
                      {(isSelected || isExisting) && (
                        <svg
                          className="h-3 w-3 text-white"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}

              {isFetching && items.length > 0 && (
                <div className="flex justify-center py-4">
                  <MoonLoader color="#2563EB" size={20} />
                </div>
              )}
              {!isFetching && !hasMore && items.length > 0 && (
                <div className="py-4 text-center text-xs text-gray-300">
                  همه موارد بارگذاری شدند ({totalCount})
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4">
          {selectedKeys.size > 0 && (
            <span className="text-sm font-medium text-blue-600">
              {selectedKeys.size} مورد انتخاب شده
            </span>
          )}
          <div className="flex flex-1 justify-end gap-3">
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              انصراف
            </button>
            <button
              onClick={handleSave}
              disabled={selectedKeys.size === 0}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              افزودن
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffManagerModal;
